<template>
  <section :class="embedded ? 'p-0' : 'bg-[#fcf5ec] p-6'">
    <div class="space-y-4">
      <header class="flex flex-col gap-3">
        <div class="flex flex-col sm:flex-row sm:items-center gap-3 justify-between">
          <h3 class="text-[22px] font-black italic uppercase text-[#111]">{{ t('regionDeals.title') }}</h3>
          <div class="flex flex-wrap items-center gap-3 w-full sm:w-auto">
            <div v-if="selectedClaimIds.length > 0" class="flex items-center gap-2">
              <button type="button" @click="exportToCsv"
                      :title="t('regionDeals.actions.export') || 'Selectie exporteren'"
                      class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-black text-white hover:bg-neutral-800 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"></path>
                </svg>
                <span class="text-xs font-bold uppercase italic">{{ t('regionDeals.actions.export') }} ({{ selectedClaimIds.length }})</span>
              </button>
              <button type="button" @click="exportAllToCsv"
                      :title="t('regionDeals.actions.exportAll') || 'Alles exporteren'"
                      class="h-10 px-3 flex items-center justify-center gap-2 border border-black bg-white text-black hover:bg-neutral-100 transition-colors">
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                </svg>
                <span class="text-xs font-bold uppercase italic">{{ t('regionDeals.actions.exportAll') }}</span>
              </button>
            </div>
            <!-- Regio selector -->
            <div class="flex items-center gap-2 text-[14px] relative">
              <select v-model="selectedRegion"
                      class="h-10 px-3 pr-8 border border-black bg-white text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none"
                      @change="onSearch">
                <option value="">Alle regio's</option>
                <option v-for="reg in availableRegions" :key="reg" :value="reg">{{ reg }}</option>
              </select>
              <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            <MmSearchInput v-model="query" :placeholder="t('regionDeals.searchPlaceholder')" @change="onSearch" />
            <div class="flex items-center gap-2 text-[14px] relative">
              <label class="text-neutral-700 sr-only">{{ t('regionDeals.filter.label') }}</label>
              <select v-model="filterRole"
                      class="h-10 px-3 pr-8 border border-black bg-white text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none">
                <option value="all">{{ t('regionDeals.filter.role.all') }}</option>
                <option value="bedrijf">{{ t('regionDeals.filter.role.bedrijf') }}</option>
                <option value="school">{{ t('regionDeals.filter.role.school') }}</option>
              </select>
              <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                   stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
              </svg>
            </div>
            <!-- Organisatie selectie afhankelijk van rol -->
            <div class="flex items-center gap-2 text-[14px]" v-if="filterRole==='bedrijf' || filterRole==='school'">
              <div class="relative">
                <label class="text-neutral-700 sr-only" :for="'orgSel-'+filterRole">{{ t('regionDeals.filter.specific') }}</label>
                <select :id="'orgSel-'+filterRole" v-model="selectedUidBound"
                        class="h-10 px-3 pr-8 border border-black bg-white text-sm font-bold uppercase italic focus:ring-0 focus:border-black appearance-none min-w-[220px]">
                  <option :value="null">{{ filterRole==='bedrijf' ? t('regionDeals.filter.allCompanies') : t('regionDeals.filter.allSchools') }}</option>
                  <option v-for="u in (filterRole==='bedrijf'?companies:schools)" :key="'opt-'+filterRole+'-'+u.id" :value="u.id">{{ u.orgName || u.displayName || u.name || ('#'+u.id) }}</option>
                </select>
                <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                     stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
              <button v-if="selectedUid" type="button" @click="clearFilter" class="text-[13px] underline text-neutral-700 hover:text-neutral-900 ml-1 whitespace-nowrap">{{ t('buttons.clear') }}</button>
            </div>
          </div>
        </div>
      </header>

      <div v-if="loading" class="text-[15px] text-neutral-600">{{ t('messages.loading') }}</div>
      <div v-else class="w-full">
        <div v-if="error" class="text-[14px] border border-[#cf524e] bg-[#fde2e0] text-[#6b1513] p-3">{{ error }}</div>
        <div v-else class="w-full">
          <template v-if="pageRows.length">
            <MmTable :columns="columns" :rows="pageRows" :sort-key="sortKeyBase" :sort-dir="sortDirBase" @sort-change="toggleSort"
                     :selectable="true" :selected-rows="selectedClaimIds" :all-selected="allPageSelected" @select-row="toggleClaimSelection" @select-all="toggleAllPageSelection">
              <template #cell:material="{ row }">
                <div class="flex items-center gap-3">
                  <div class="relative">
                    <img :src="row.thumbnail || row.image || placeholder" alt="" class="h-16 w-16 object-cover border border-neutral-300 bg-[#f9f4ec]"/>
                    <div v-if="isRequested(row)" class="absolute top-0 right-0 bg-black text-white text-[8px] px-1 py-0.5 font-bold uppercase">Vraag</div>
                  </div>
                  <div class="min-w-0 max-w-[14rem] sm:max-w-[16rem] md:max-w-[18rem]">
                    <div class="font-semibold text-neutral-900 truncate" :title="decodeHTML(row.materialTitle || '—')">{{ decodeHTML(row.materialTitle || '—') }}</div>
                    <div class="text-[13px] text-neutral-600 truncate">#{{ padId(row.materialId || row.id) }}</div>
                  </div>
                </div>
              </template>
              <template #cell:claimNumber="{ row }"><span>{{ row.claimNumber || '—' }}</span></template>
              <template #cell:status="{ row }">
                <span class="inline-block rounded-sm px-2 py-1 text-xs font-bold uppercase tracking-widest" :class="statusClass(row.status)">{{ prettyStatus(row.status) }}</span>
              </template>
              <template #cell:waarde="{ row }">{{ formatCurrency(row.waarde) }}</template>
              <template #cell:provider="{ row }">
                <div class="flex flex-col gap-1">
                  <template v-if="row.provider?.name">
                    <a class="underline hover:no-underline font-semibold" :href="toCompanies(row.provider)">{{ row.provider.name }}</a>
                  </template>
                  <template v-else>—</template>
                  <div class="flex flex-wrap gap-1">
                    <span v-for="(reg, idx) in (row.provider?.regions || [])" :key="idx" class="inline-block px-1 py-0.5 bg-neutral-100 text-[9px] font-bold uppercase rounded-sm border border-neutral-200 leading-none">
                      {{ reg }}
                    </span>
                  </div>
                </div>
              </template>
              <template #cell:claimedBy="{ row }">
                <div class="flex flex-col gap-1">
                  <template v-if="row.claimedBy?.name">
                    <a class="underline hover:no-underline font-semibold" :href="toSchools(row.claimedBy)">{{ row.claimedBy.name }}</a>
                  </template>
                  <template v-else>—</template>
                  <div class="flex flex-wrap gap-1">
                    <span v-for="(reg, idx) in (row.claimedBy?.regions || [])" :key="idx" class="inline-block px-1 py-0.5 bg-neutral-100 text-[9px] font-bold uppercase rounded-sm border border-neutral-200 leading-none">
                      {{ reg }}
                    </span>
                  </div>
                </div>
              </template>
              <template #cell:date="{ row }">{{ formatDate(row.date) }}</template>
              <template #cell:actions="{ row }">
                <div class="flex items-center gap-2">
                  <button type="button" @click="openDetails(row)" class="inline-flex items-center justify-center px-3 py-2 border border-black bg-white hover:bg-black hover:text-white text-sm font-bold uppercase italic">{{ t('regionDeals.actions.details') }}</button>
                  <a v-if="['completed','approved'].includes(String(row.status||'').toLowerCase())" :href="downloadUrl(row.materialId || row.id)" class="inline-flex items-center justify-center px-3 py-2 border border-black bg-white text-black hover:bg-black hover:text-white text-sm font-bold uppercase italic" target="_blank">PDF</a>
                </div>
              </template>
            </MmTable>
            <MmPagination :page="page" :pages="pages" :show-per-page="true" :per-page="perPage" :per-page-options="[10,20,50]" @page-change="goTo" @per-page-change="onPerPageChange" />
          </template>
          <div v-else class="text-[15px] text-neutral-700">{{ t('regionDeals.empty') }}</div>
        </div>
      </div>
    </div>

    <!-- Details modal -->
    <MmModal v-model="openDetailsModal" :title="t('regionDeals.modal.title')">
      <div v-if="current" class="space-y-4">
        <div class="min-w-0">
          <h4 class="m-0 font-semibold">{{ current.materialTitle || '—' }}</h4>
          <dl class="mt-3 grid grid-cols-[auto_1fr] gap-x-2 gap-y-1 text-sm">
            <dt class="text-neutral-600">{{ t('regionDeals.modal.adId') }}</dt><dd><span>{{ padId(current.materialId || current.id) }}</span></dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.claimNumber') }}</dt><dd><span>{{ current.claimNumber || '—' }}</span></dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.waarde') }}</dt><dd>{{ formatCurrency(current.waarde) }}</dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.status') }}</dt>
            <dd>
              <span class="inline-block rounded-sm px-2 py-1 text-xs font-bold uppercase tracking-widest" :class="statusClass(current.status)">{{ prettyStatus(current.status) }}</span>
            </dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.provider') }}</dt>
            <dd>
              <div class="flex flex-col gap-1">
                <template v-if="current.provider?.name">
                  <a class="underline" :href="toCompanies(current.provider)">{{ current.provider.name }}</a>
                  <template v-if="current.provider?.email">
                    <span class="text-neutral-600"> (<a class="underline text-neutral-600" :href="'mailto:'+current.provider.email">{{ current.provider.email }}</a>)</span>
                  </template>
                </template>
                <template v-else>—</template>
                <div class="flex flex-wrap gap-1">
                  <span v-for="(reg, idx) in (current.provider?.regions || [])" :key="idx" class="inline-block px-1 py-0.5 bg-neutral-100 text-[9px] font-bold uppercase rounded-sm border border-neutral-200 leading-none">
                    {{ reg }}
                  </span>
                </div>
              </div>
            </dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.claimedBy') }}</dt>
            <dd>
              <div class="flex flex-col gap-1">
                <template v-if="current.claimedBy?.name">
                  <a class="underline" :href="toSchools(current.claimedBy)">{{ current.claimedBy.name }}</a>
                  <template v-if="current.claimedBy?.email">
                    <span class="text-neutral-600"> (<a class="underline text-neutral-600" :href="'mailto:'+current.claimedBy.email">{{ current.claimedBy.email }}</a>)</span>
                  </template>
                </template>
                <template v-else>—</template>
                <div class="flex flex-wrap gap-1">
                  <span v-for="(reg, idx) in (current.claimedBy?.regions || [])" :key="idx" class="inline-block px-1 py-0.5 bg-neutral-100 text-[9px] font-bold uppercase rounded-sm border border-neutral-200 leading-none">
                    {{ reg }}
                  </span>
                </div>
              </div>
            </dd>
            <dt class="text-neutral-600">{{ t('regionDeals.modal.date') }}</dt><dd>{{ formatDate(current.date) }}</dd>
            <template v-if="String(current.status||'').toLowerCase()==='cancelled'">
              <dt class="text-neutral-600">{{ t('regionDeals.modal.cancelledOn') }}</dt><dd>{{ formatDate(current.releaseDate) }}</dd>
              <dt class="text-neutral-600">{{ t('regionDeals.modal.reason') }}</dt><dd>{{ current.releaseReason || '—' }}</dd>
            </template>
          </dl>
          <div v-if="['completed','approved'].includes(String(current.status||'').toLowerCase())" class="mt-4">
            <a :href="downloadUrl(current.materialId || current.id)" class="inline-flex items-center justify-center px-4 py-2 border border-black bg-black text-white hover:bg-white hover:text-black text-sm font-bold uppercase italic" target="_blank">
              {{ t('buttons.downloadPakbon') || 'Download Pakbon' }}
            </a>
          </div>
        </div>

        <!-- Coördinator actie: status wijzigen -->
        <div class="pt-4 border-t border-neutral-200 mt-4">
          <div class="mm-admin-block space-y-3">
            <h5 class="text-black font-black italic uppercase text-[15px] mb-2">{{ t('regionDeals.modal.manageStatus') || 'Status wijzigen' }}</h5>
            <div class="space-y-2">
              <p class="text-sm font-medium text-neutral-700">{{ t('regionDeals.modal.selectStatus') || 'Kies nieuwe status' }}</p>
              <div class="relative">
                <select v-model="selectedClaimStatus" class="mm-admin-block__select w-full h-10 px-3 pr-8 border border-black bg-white text-sm appearance-none">
                  <option value="">— {{ t('regionDeals.modal.selectStatus') || 'Kies nieuwe status' }} —</option>
                  <option value="pending">{{ t('regionDeals.status.pending') }}</option>
                  <option value="completed">{{ t('regionDeals.status.completed') }}</option>
                  <option value="rejected">{{ t('regionDeals.status.rejected') }}</option>
                  <option value="cancelled">{{ t('regionDeals.status.cancelled') }}</option>
                </select>
                <svg class="absolute right-2 top-1/2 -translate-y-1/2 w-4 h-4 pointer-events-none" fill="none"
                     stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
            <div class="flex flex-wrap gap-2">
              <button type="button" class="mm-admin-block__button" :disabled="managingClaim || !selectedClaimStatus" @click="updateClaimStatus">
                {{ managingClaim ? t('messages.loading') : (t('regionDeals.modal.executeAction') || 'Uitvoeren') }}
              </button>
            </div>
            <div v-if="manageClaimError" class="mt-2 text-[13px] border border-[#cf524e] bg-[#fde2e0] text-[#6b1513] p-2">{{ manageClaimError }}</div>
          </div>
        </div>
      </div>
    </MmModal>
  </section>
</template>

<script>
import WpApiClient from '../lib/wp-api.js'
import MmTable from './MmTable.vue'
import MmPagination from './MmPagination.vue'
import MmSearchInput from './MmSearchInput.vue'
import MmModal from './MmModal.vue'
import { t } from '../i18n'
import { statusBadgeClass } from '../lib/status-utils'
import { formatDateNL, padId as padIdUtil, decodeHTML } from '../lib/format-utils'
export default {
  name: 'MmRegionClaims',
  components: { MmTable, MmPagination, MmSearchInput, MmModal },
  props: { 
    embedded: { type: Boolean, default: false },
    uid: { type: [String, Number], default: null }
  },
  data(){
    return {
      // Expose translator for template usage
      t,
      loading: true,
      error: '',
      items: [],
      api: null,
      page: 1,
      perPage: 10,
      query: '',
      selectedRegion: '',
      sortKey: 'date_desc',
      filterRole: 'all',
      openDetailsModal: false,
      current: null,
      selectedClaimIds: [],
      // Individuele filter via URL (?type=bedrijf|school&uid=...&q=...)
      selectedType: '',
      selectedUid: null,
      selectedName: '',
      companies: [],
      schools: [],
      regionsFromApi: [],
      selectedClaimStatus: '',
      managingClaim: false,
      manageClaimError: '',
      autoOpenClaimId: null,
      placeholder: 'data:image/svg+xml;utf8,' + encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 80 80"><rect width="80" height="80" fill="#f3f4f6"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="#9ca3af" font-family="sans-serif" font-size="10">—</text></svg>`),
    }
  },
  computed: {
    pages(){
      const p = Math.max(1, Math.ceil(this.filtered.length / this.perPage))
      return Array.from({ length: p }, (_, i) => i + 1)
    },
    availableRegions(){
      if (Array.isArray(this.regionsFromApi) && this.regionsFromApi.length) {
        return this.regionsFromApi
      }
      const set = new Set()
      this.items.forEach(it => {
        if (it.provider && Array.isArray(it.provider.regions)) it.provider.regions.forEach(r => set.add(r))
        if (it.claimedBy && Array.isArray(it.claimedBy.regions)) it.claimedBy.regions.forEach(r => set.add(r))
      })
      return Array.from(set).sort()
    },
    sortKeyBase(){ return (String(this.sortKey).split('_')[0] || 'date') },
    sortDirBase(){ return (String(this.sortKey).split('_')[1] || 'desc') },
    // Binding helper zodat native <select> ook een null-waarde kan hebben
    selectedUidBound: {
      get(){ return this.selectedUid },
      set(v){
        const val = (v===null || v===undefined || v==='') ? null : Number(v)
        if (val) {
          this.selectedUid = val
          this.selectedType = (this.filterRole==='bedrijf' || this.filterRole==='school') ? this.filterRole : ''
        } else {
          this.selectedUid = null
          this.selectedType = (this.filterRole==='all') ? '' : this.filterRole
        }
        this.updateUrlFromSelection()
        this.reload()
      }
    },
    filtered(){
      const q = String(this.query||'').trim().toLowerCase()
      let arr = Array.isArray(this.items) ? this.items.slice() : []
      // Filteren op partijtype indien geselecteerd
      const fr = String(this.filterRole||'all')
      if (fr === 'bedrijf') {
        arr = arr.filter(d => !!d?.provider)
      } else if (fr === 'school') {
        arr = arr.filter(d => !!d?.claimedBy)
      }
      // Individuele selectie op basis van URL-parameters (server filtert al, maar client borgt het ook)
      if (this.selectedUid && this.selectedType === 'bedrijf') {
        arr = arr.filter(d => String(d?.provider?.id||'') === String(this.selectedUid))
      } else if (this.selectedUid && this.selectedType === 'school') {
        arr = arr.filter(d => String(d?.claimedBy?.id||'') === String(this.selectedUid))
      }
      if (this.selectedRegion) {
        arr = arr.filter(d => {
          const pRegs = d.provider?.regions || []
          const cRegs = d.claimedBy?.regions || []
          return pRegs.includes(this.selectedRegion) || cRegs.includes(this.selectedRegion)
        })
      }
      if (q) {
        arr = arr.filter(d => {
          const title = String(d.materialTitle||'').toLowerCase()
          const prov = String(d.provider?.name||'').toLowerCase()
          const claimer = String(d.claimedBy?.name||'').toLowerCase()
          const id = String(d.materialId||d.id||'')
          return title.includes(q) || prov.includes(q) || claimer.includes(q) || id.includes(q)
        })
      }
      // sort
      const key = this.sortKeyBase
      const dir = this.sortDirBase
      arr.sort((a,b)=>{
        let av, bv
        switch(key){
          case 'title': av = String(a.materialTitle||'').toLowerCase(); bv = String(b.materialTitle||'').toLowerCase(); break
          case 'status': av = String(a.status||'').toLowerCase(); bv = String(b.status||'').toLowerCase(); break
          case 'date': default:
            av = Date.parse(a.date||'')||0; bv = Date.parse(b.date||'')||0; break
        }
        if (av===bv) return 0
        const res = av < bv ? -1 : 1
        return dir==='asc' ? res : -res
      })
      return arr
    },
    pageRows(){
      const start = (this.page-1) * this.perPage
      return this.filtered.slice(start, start + this.perPage)
    },
    allPageSelected(){
      if (!this.pageRows.length) return false
      return this.pageRows.every(r => this.selectedClaimIds.includes(r.id))
    },
    columns(){
      return [
        { key: 'material', label: t('regionDeals.columns.material'), sortable: true },
        { key: 'claimNumber', label: t('regionDeals.columns.claimNumber'), sortable: true },
        { key: 'provider', label: t('regionDeals.columns.provider'), sortable: false },
        { key: 'claimedBy', label: t('regionDeals.columns.claimedBy'), sortable: false },
        { key: 'status', label: t('regionDeals.columns.status'), sortable: true },
        { key: 'waarde', label: t('regionDeals.columns.waarde'), sortable: true },
        { key: 'date', label: t('regionDeals.columns.date'), sortable: true },
        { key: 'actions', label: t('regionDeals.columns.actions'), sortable: false },
      ]
    }
  },
  methods: {
    decodeHTML,
    toggleClaimSelection(id, selected){
      if (selected) {
        if (!this.selectedClaimIds.includes(id)) this.selectedClaimIds.push(id)
      } else {
        this.selectedClaimIds = this.selectedClaimIds.filter(x => x !== id)
      }
    },
    toggleAllPageSelection(selected){
      if (selected) {
        this.pageRows.forEach(r => {
          if (!this.selectedClaimIds.includes(r.id)) this.selectedClaimIds.push(r.id)
        })
      } else {
        const pageIds = this.pageRows.map(r => r.id)
        this.selectedClaimIds = this.selectedClaimIds.filter(id => !pageIds.includes(id))
      }
    },
    exportToCsv(){
      if (!this.selectedClaimIds.length) return
      const selectedItems = this.items.filter(it => this.selectedClaimIds.includes(it.id))
      this.doExport(selectedItems, 'claims-export')
    },
    async exportAllToCsv(){
      this.loading = true
      try {
        const payload = { nonce: window?.materiaalAjax?.nonce }
        if (this.selectedUid && this.selectedType === 'bedrijf') {
          payload.providerId = this.selectedUid
        } else if (this.selectedUid && this.selectedType === 'school') {
          payload.claimerId = this.selectedUid
        }
        const res = await this.api.postAjax('materiaal_list_region_claims', payload)
        if (res && res.ok && Array.isArray(res.items)) {
          this.doExport(res.items, 'all-claims')
        }
      } catch (e) {
        console.error('Export all failed', e)
      } finally {
        this.loading = false
      }
    },
    doExport(itemsToExport, filenamePrefix = 'claims-export'){
      if (!itemsToExport.length) return

      const headers = [
        'ID',
        t('regionDeals.modal.adId') || 'Advertentie ID',
        t('regionDeals.columns.claimNumber') || 'Claimnr',
        t('regionDeals.columns.material') || 'Materiaal Titel',
        t('regionDeals.columns.status') || 'Status',
        t('regionDeals.columns.waarde') || 'Waarde',
        t('regionDeals.columns.date') || 'Datum',
        t('regionDeals.modal.cancelledOn') || 'Geannuleerd op',
        t('regionDeals.modal.reason') || 'Reden',
        t('regionDeals.columns.provider') || 'Aanbieder Naam',
        t('regionDeals.csv.providerEmail') || 'Aanbieder Email',
        t('regionDeals.csv.providerPhone') || 'Aanbieder Telefoon',
        t('regionDeals.csv.providerRegions') || 'Aanbieder Regio\'s',
        t('regionDeals.columns.claimedBy') || 'Geclaimd door Naam',
        t('regionDeals.csv.claimerEmail') || 'Claimer Email',
        t('regionDeals.csv.claimerPhone') || 'Claimer Telefoon',
        t('regionDeals.csv.claimerRegions') || 'Claimer Regio\'s'
      ]

      const rows = itemsToExport.map(it => [
        it.id || '',
        this.padId(it.materialId || it.id),
        it.claimNumber || '',
        it.materialTitle || it.title || '',
        this.prettyStatus(it.status),
        this.formatCurrency(it.waarde),
        this.formatDate(it.date),
        it.releaseDate ? this.formatDate(it.releaseDate) : '',
        it.releaseReason || '',
        it.provider?.name || '',
        it.provider?.email || '',
        it.provider?.phone || '',
        (it.provider?.regions || []).join(', '),
        it.claimedBy?.name || '',
        it.claimedBy?.email || '',
        it.claimedBy?.phone || '',
        (it.claimedBy?.regions || []).join(', ')
      ])

      let csvContent = headers.join(';') + '\n'
      rows.forEach(row => {
        const rowStr = row.map(val => {
          const s = String(val).replace(/"/g, '""')
          return s.includes(';') || s.includes('"') || s.includes('\n') ? `"${s}"` : s
        }).join(';')
        csvContent += rowStr + '\n'
      })

      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' })
      const url = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.setAttribute('href', url)
      link.setAttribute('download', `${filenamePrefix}-${new Date().toISOString().slice(0,10)}.csv`)
      link.style.visibility = 'hidden'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    },
    onPerPageChange(val){ this.perPage = Number(val)||this.perPage; this.goFirst() },
    async loadPicklists(){
      try{
        this.api = this.api || new WpApiClient()
        const [cRes, sRes] = await Promise.all([
          this.api.postAjax('materiaal_list_region_users', { role: 'bedrijf', nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined }).catch(()=>({})),
          this.api.postAjax('materiaal_list_region_users', { role: 'school', nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined }).catch(()=>({})),
        ])
        const companies = Array.isArray(cRes?.users) ? cRes.users : (Array.isArray(cRes?.items)?cRes.items:[])
        const schools = Array.isArray(sRes?.users) ? sRes.users : (Array.isArray(sRes?.items)?sRes.items:[])
        const sortByName = (a,b)=>{
          const av = String(a.orgName||a.displayName||a.name||'').toLowerCase()
          const bv = String(b.orgName||b.displayName||b.name||'').toLowerCase()
          if (av===bv) return 0; return av<bv?-1:1
        }
        this.companies = Array.isArray(companies) ? companies.slice().sort(sortByName) : []
        this.schools = Array.isArray(schools) ? schools.slice().sort(sortByName) : []
      }catch(_){ this.companies=[]; this.schools=[] }
    },
    applyUrlQuery(){
      try{
        const params = new URLSearchParams(window.location.search||'')
        const t = (params.get('type')||'').toLowerCase()
        const uid = params.get('uid')
        const q = params.get('q')
        const claimId = params.get('claim_id')

        if (t==='bedrijf' || t==='school') {
          this.selectedType = t
          // Zet hoofd-filter gelijk zodat de tabel zich aansluit bij keuze
          this.filterRole = t
        }
        if (uid) {
          const n = Number(uid)
          this.selectedUid = Number.isFinite(n) ? n : null
        }
        if (q) this.selectedName = q
        
        if (claimId) {
          this.autoOpenClaimId = Number(claimId)
        }
      }catch(_){ }
    },
    updateUrlFromSelection(){
      try{
        const url = new URL(window.location.href)
        if (this.selectedUid && (this.selectedType==='bedrijf' || this.selectedType==='school')){
          url.searchParams.set('type', this.selectedType)
          url.searchParams.set('uid', String(this.selectedUid))
          const name = this.selectedType==='bedrijf' ? (this.companies.find(u=>String(u.id)===String(this.selectedUid))?.orgName||'') : (this.schools.find(u=>String(u.id)===String(this.selectedUid))?.orgName||'')
          if (name) url.searchParams.set('q', name); else url.searchParams.delete('q')
        } else {
          url.searchParams.delete('type')
          url.searchParams.delete('uid')
          url.searchParams.delete('q')
        }
        window.history.replaceState(null, '', url.toString())
      }catch(_){ }
    },
    clearFilter(){
      this.selectedType = ''
      this.selectedUid = null
      this.selectedName = ''
      this.filterRole = 'all'
      try{
        const url = new URL(window.location.href)
        url.searchParams.delete('type')
        url.searchParams.delete('uid')
        url.searchParams.delete('q')
        window.history.replaceState(null, '', url.toString())
      }catch(_){ }
      this.reload()
    },
    toCompanies(provider){
      try {
        const q = provider?.name || provider?.email || ''
        const id = provider?.id || ''
        const params = new URLSearchParams()
        if (q) params.set('q', String(q))
        if (id) params.set('uid', String(id))
        const qs = params.toString()
        return `/account/regio-bedrijven${qs ? ('?' + qs) : ''}`
      } catch { return '/account/regio-bedrijven' }
    },
    toSchools(claimer){
      try {
        const q = claimer?.name || claimer?.email || ''
        const id = claimer?.id || ''
        const params = new URLSearchParams()
        if (q) params.set('q', String(q))
        if (id) params.set('uid', String(id))
        const qs = params.toString()
        return `/account/regio-scholen${qs ? ('?' + qs) : ''}`
      } catch { return '/account/regio-scholen' }
    },
    async load(){
      this.loading = true; this.error=''
      try{
        this.api = this.api || new WpApiClient()
        const payload = { nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined }
        if (this.selectedUid && this.selectedType === 'bedrijf') {
          payload.providerId = this.selectedUid
        } else if (this.selectedUid && this.selectedType === 'school') {
          payload.claimerId = this.selectedUid
        }
        const res = await this.api.postAjax('materiaal_list_region_claims', payload)
        if (res && res.ok) {
          const raw = Array.isArray(res.items) ? res.items : []
          this.items = raw.map(it => ({
            ...it,
            materialTitle: decodeHTML(it.materialTitle || it.title || ''),
            provider: it.provider ? {
              ...it.provider,
              name: decodeHTML(it.provider.name || '')
            } : null,
            claimedBy: it.claimedBy ? {
              ...it.claimedBy,
              name: decodeHTML(it.claimedBy.name || '')
            } : null
          }))
          if (Array.isArray(res.regions)) {
            this.regionsFromApi = res.regions
          }

          if (this.autoOpenClaimId) {
            const found = this.items.find(it => Number(it.id) === this.autoOpenClaimId)
            if (found) {
              this.openDetails(found)
            }
            this.autoOpenClaimId = null
          }
        } else {
          this.error = (res && (res.error||res.message)) || 'Laden mislukt'
          this.items = []
        }
      } catch(e){ this.error = e?.message || 'Laden mislukt'; this.items = [] }
      this.loading = false
    },
    async reload(){ await this.load() },
    goTo(p){ this.page = p },
    goFirst(){ this.page = 1 },
    onSearch(){ this.goFirst() },
    toggleSort(key){
      const base = String(key)
      const curBase = this.sortKeyBase
      const curDir = this.sortDirBase
      if (base === curBase) this.sortKey = `${base}_${curDir==='asc'?'desc':'asc'}`
      else this.sortKey = `${base}_asc`
    },
    applySort(){ /* removed: sorting via table headers */ },
    openDetails(row){ this.current = { ...row }; this.openDetailsModal = true; this.selectedClaimStatus = ''; this.manageClaimError = '' },
    async updateClaimStatus(){
      if (!this.current || !this.selectedClaimStatus) return
      this.managingClaim = true
      this.manageClaimError = ''
      try {
        this.api = this.api || new WpApiClient()
        const res = await this.api.postAjax('materiaal_update_claim', {
          materialId: this.current.materialId || this.current.id,
          status: this.selectedClaimStatus,
          nonce: (window.materiaalAjax && materiaalAjax.nonce) ? materiaalAjax.nonce : undefined,
        })
        if (res && res.ok) {
          this.current = { ...this.current, status: this.selectedClaimStatus }
          this.items = this.items.map(it => {
            const mid = it.materialId || it.id
            const cmid = this.current.materialId || this.current.id
            if (String(mid) === String(cmid)) return { ...it, status: this.selectedClaimStatus }
            return it
          })
          this.selectedClaimStatus = ''
        } else {
          this.manageClaimError = (res && res.error) || 'Actie mislukt'
        }
      } catch(e) {
        this.manageClaimError = e.message || 'Actie mislukt'
      } finally {
        this.managingClaim = false
      }
    },
    formatDate(iso){ return formatDateNL(iso) },
    padId(val){ return padIdUtil(val) },
    formatCurrency(val) {
      if (val === null || val === undefined || val === '') return '—'
      const n = Number(val)
      if (isNaN(n)) return val
      return new Intl.NumberFormat('nl-NL', { style: 'currency', currency: 'EUR' }).format(n)
    },
    isRequested(row) {
      if (!row) return false
      const type = String(row.mm_type || row.type || '').toLowerCase()
      return type === 'requested' || type === 'gevraagd'
    },
    downloadUrl(materialId) {
      const baseUrl = window.materiaalMaatjesData?.ajaxUrl || window.materiaalAjax?.ajaxurl || '/wp-admin/admin-ajax.php'
      const nonce = window?.materiaalAjax?.nonce || ''
      return `${baseUrl}?action=materiaal_download_claim_pdf&material_id=${materialId}&nonce=${nonce}`
    },
    prettyStatus(s){
      const v = String(s||'').toLowerCase()
      if (v==='pending' || v==='new' || v==='in_progress') return t('regionDeals.status.pending')
      if (v==='approved' || v==='accepted' || v==='completed') return t('regionDeals.status.completed')
      if (v==='rejected') return t('regionDeals.status.rejected')
      if (v==='cancelled' || v==='canceled') return t('regionDeals.status.cancelled')
      return s || '—'
    },
    statusClass(s){ return statusBadgeClass(s) },
  },
  mounted(){ this.applyUrlQuery(); this.loadPicklists(); this.load() }
}
</script>

<style scoped>
.mm-admin-block {
  background-color: #EEDCC5 !important;
  border: 1px solid #000 !important;
  padding: 12px !important;
  border-radius: 6px !important;
}
.mm-admin-block__button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 10px 14px;
  font-weight: 600;
  cursor: pointer;
  border-radius: 8px;
  border: 1px solid #000;
  background-color: #fff;
  color: #000;
  font-size: 15px;
}
.mm-admin-block__button:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}
.mm-admin-block__select {
  background-color: #fff;
  border: 1px solid #000;
  border-radius: 4px;
  color: #000;
  font-size: 14px;
}
</style>
